import { OrganizationBreadcrumbs } from "@knowfully/ui.common";
import { CircularProgress, SxProps, Theme, Typography, useTheme } from "@mui/material";
import useGlobalContext from "hooks/globalData";
import useOrganizationSwitch from "hooks/useOrganizationSwitch";
import { useCallback, useEffect } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  OrganizationTreeItem,
  ResponseOfOrganizationTreeItem,
} from "services/KnowfullyAdminClient";
import contextStore, { ContextStore } from "stores/contextStore";
import { OrganizationContextStorage } from "utils/organizationContextStorage";

const useStyles = (
  theme: Theme,
  isMobile: boolean
): { [key: string]: SxProps } => ({
  breadcrumb: {
    textTransform: "none",
    textAlign: "left",
    fontSize: isMobile ? "16px" : "18px",
    color: isMobile
      ? theme.palette.secondary.contrastText
      : theme.palette.secondary.light,
    "&.Mui-disabled": {
      color: isMobile
        ? theme.palette.secondary.contrastText
        : theme.palette.secondary.light,
    },
  },
  expandIcon: {
    height: "fit-content",
    fontSize: isMobile ? "16px" : "18px",
    color: isMobile
      ? theme.palette.secondary.contrastText
      : theme.palette.secondary.light,
    "&.Mui-disabled": {
      color: isMobile
        ? theme.palette.secondary.contrastText
        : theme.palette.secondary.light,
    },
  },
});

const findFirstAccessibleOrg = (
  node: OrganizationTreeItem
): OrganizationTreeItem | null => {
  if (!node.isDisplayOnly) {
    return node;
  }

  const sortedChildren = [...(node.organizations ?? [])].sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? "")
  );

  for (const child of sortedChildren) {
    const found = findFirstAccessibleOrg(child);
    if (found) {
      return found;
    }
  }

  return null;
};


type OrganizationBreadcrumbsWrapperProps = {
  isMobile: boolean;
};

const OrganizationBreadcrumbsWrapper = ({
  isMobile,
}: OrganizationBreadcrumbsWrapperProps) => {
  const styles = useStyles(useTheme(), isMobile);
  const { organizationService } = useGlobalContext();
  const { formatMessage: t } = useIntl();
  const { organizationId, organizationIdDisabled } = contextStore.useState();
  const { switchOrganization } = useOrganizationSwitch();

  const onChangeOrganization = useCallback((organization: OrganizationTreeItem) => {
    // OrganizationTreeItem does not own application-instance selection, so switching organizations clears it.
    const contextObject: ContextStore = {
      organizationId: organization.id,
      organizationName: organization.name,
      organizationTypeId: organization.organizationTypeId,
      organizationIdDisabled: false,
    };

    OrganizationContextStorage.set(contextObject);
    contextStore.update(() => contextObject);

    // A change from one organization to another is switched here. When nothing was selected,
    // the owner in App (useOrganizationContext) switches once the context is filled.
    if (organizationId && organization.id) {
      switchOrganization(organization.id);
    }
  }, [organizationId, switchOrganization]);

  const {
    isLoading,
    isError,
    data: organizationTreeResponse,
  } = useQuery<ResponseOfOrganizationTreeItem>(
    "organizationService.getOrganizationTree",
    () => organizationService.getOrganizationTree()
  );

  useEffect(() => {
    if (!isLoading && organizationTreeResponse?.data && !organizationId) {
      const initialOrg = findFirstAccessibleOrg(organizationTreeResponse.data);
      if (initialOrg) {
        onChangeOrganization(initialOrg);
      }
    }
  }, [isLoading, organizationTreeResponse, organizationId, onChangeOrganization]);

  if (isError || organizationTreeResponse?.responseReason != null) {
    return (
      <Typography color="error">
        {t({ id: "organizationBreadcrumbsFetchFailed" })}
      </Typography>
    );
  }

  const loading = <CircularProgress size={16} color="inherit" aria-label={t({ id: "loading" })} />;

  if (!organizationTreeResponse?.data) {
    return isLoading ? loading : null;
  }

  // Until an organization is picked the breadcrumb would draw the root as if it were chosen, while
  // a form opened then has no organization yet. A loaded tree always holds one the user can pick.
  if (!organizationId) {
    return loading;
  }

  return (
    <OrganizationBreadcrumbs
      organizationTree={organizationTreeResponse.data}
      selectedOrganizationId={organizationId}
      onChange={onChangeOrganization}
      label={t({ id: "organization" })}
      disabled={organizationIdDisabled}
      nonClientOrgnanizationsDisabled={false}
      displayOnlyDisabled={false}
      sxBreadcrumb={styles.breadcrumb}
      sxExpandIcon={styles.expandIcon}
    />
  );
};

export default OrganizationBreadcrumbsWrapper;
